// ReportMetricVSMetricBars.jsx

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Container, Table, TableBody, Typography } from "@mui/material";
import EChartsReact from "echarts-for-react";

import { tooltip } from "../../../helpers/eCharts";
import {
    DISPLAY_ELEMENT_HELPER,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_WEEK,
    GROUP_DATE_YEAR,
    GROUP_COUNTRIES,
    COMPARISON_FILTER,
} from "../../../helpers/filters";
import { getWeekNumberSince2000, getWeekYear } from "../../../helpers/dates";
import HoverPaper from "../HoverPaper";
import NoDataRow from "../../NoDataRow";
import Colours, { METRIC_COLORS } from "../../../helpers/colours";
import InfoIcon from "../../InfoIcon";

const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const REPORT_METRIC_VS_METRIC_BARS = "REPORT_METRIC_VS_METRIC_BARS";
const ReportMetricVSMetricBars = ({
    data,
    title,
    metrics = ["revenueRoom", "bookingsRooms"],
    aggregation = "Month",
    showLastYear = false,
    showAllXAxisLabels = false,
    othersFrom = null,
    othersOrderBy = "auto",
    comparisonText = "last year",
    description,
    emptyMessage = "No data for these filters",
    currentSeriesLabel,
    comparisonSeriesLabel,
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const chartData = useMemo(() => {
        const grouped = {};

        Object.values(data ?? {}).forEach(
            ({ keys, result, resultOneYearBefore }) => {
                const k = Object.fromEntries(keys ?? []);

                const country = String(k[GROUP_COUNTRIES] ?? "Unknown");

                const year = Number(
                    k[GROUP_DATE_YEAR] ?? new Date().getUTCFullYear(),
                );
                const month = Number(k[GROUP_DATE_MONTH] ?? 1);
                const day = Number(k[GROUP_DATE_DAY] ?? 1);
                const date = new Date(Date.UTC(year, month - 1, day));
                const weekInfo = getWeekYear(date);
                const week = Number(k[GROUP_DATE_WEEK] ?? weekInfo.week);

                const weekdayIndex = (date.getUTCDay() + 6) % 7;

                const key =
                    aggregation === "Country"
                        ? country
                        : aggregation === "Day"
                          ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
                          : aggregation === "Week"
                            ? `${weekInfo.year} - Week #${week}`
                            : aggregation === "Weekday"
                              ? WEEKDAYS[weekdayIndex]
                              : aggregation === "Year"
                                ? `${year}`
                                : `${year}-${String(month).padStart(2, "0")}`;

                const label =
                    aggregation === "Country"
                        ? country
                        : aggregation === "Day"
                          ? `${day} ${MONTHS[month - 1]} ${year}`
                          : aggregation === "Week"
                            ? `Week #${week} - ${weekInfo.year}`
                            : aggregation === "Weekday"
                              ? WEEKDAYS[weekdayIndex]
                              : aggregation === "Year"
                                ? `${year}`
                                : `${MONTHS[month - 1]} ${year}`;

                const sort =
                    aggregation === "Country"
                        ? country
                        : aggregation === "Day"
                          ? year * 10000 + month * 100 + day
                          : aggregation === "Week"
                            ? weekInfo.year * 100 + week
                            : aggregation === "Weekday"
                              ? weekdayIndex
                              : aggregation === "Year"
                                ? year
                                : year * 100 + month;

                grouped[key] = grouped[key] ?? {
                    label,
                    sort,
                };

                metrics.forEach((metric) => {
                    grouped[key][metric] =
                        (grouped[key][metric] ?? 0) +
                        (Number(result?.[metric]) || 0);

                    grouped[key][`${metric}LastYear`] =
                        (grouped[key][`${metric}LastYear`] ?? 0) +
                        (Number(resultOneYearBefore?.[metric]) || 0);
                });
            },
        );

        let rows = Object.values(grouped).sort((a, b) =>
            typeof a.sort === "string"
                ? a.sort.localeCompare(b.sort)
                : a.sort - b.sort,
        );
        rows = rows.filter((row) =>
            metrics.some(
                (metric) =>
                    (Number(row[metric]) || 0) !== 0 ||
                    (showLastYear &&
                        (Number(row[`${metric}LastYear`]) || 0) !== 0),
            ),
        );

        if (othersFrom && rows.length >= othersFrom) {
            const sortMetric = metrics.includes(othersOrderBy)
                ? othersOrderBy
                : null;

            if (sortMetric) {
                rows = [...rows].sort(
                    (a, b) =>
                        (Number(b[sortMetric]) || 0) -
                        (Number(a[sortMetric]) || 0),
                );
            } else {
                const scores = new Map(rows.map((row) => [row, 0]));

                metrics.forEach((metric) => {
                    [...rows]
                        .sort(
                            (a, b) =>
                                (Number(b[metric]) || 0) -
                                (Number(a[metric]) || 0),
                        )
                        .forEach((row, index) => {
                            scores.set(row, scores.get(row) + index);
                        });
                });

                rows = [...rows].sort(
                    (a, b) => (scores.get(a) ?? 0) - (scores.get(b) ?? 0),
                );
            }

            const visibleRows = rows.slice(0, othersFrom - 1);
            const othersRows = rows.slice(othersFrom - 1);

            const others = {
                label: "Others",
                sort: "Others",
            };

            metrics.forEach((metric) => {
                others[metric] = othersRows.reduce(
                    (total, row) => total + (Number(row[metric]) || 0),
                    0,
                );

                others[`${metric}LastYear`] = othersRows.reduce(
                    (total, row) =>
                        total + (Number(row[`${metric}LastYear`]) || 0),
                    0,
                );
            });

            rows = [...visibleRows, others];
        }

        return {
            xAxisData: rows.map((row) => row.label),
            series: metrics.flatMap((metric, index) => {
                const defaultName =
                    DISPLAY_ELEMENT_HELPER[metric]?.short ?? metric;
                const name =
                    currentSeriesLabel && metrics.length === 1
                        ? currentSeriesLabel
                        : defaultName;
                const lastYearName =
                    comparisonSeriesLabel && metrics.length === 1
                        ? comparisonSeriesLabel
                        : `${name} ${COMPARISON_FILTER[comparisonText]?.label ?? comparisonText}`;
                const color = METRIC_COLORS[index % METRIC_COLORS.length];
                return [
                    {
                        type: "bar",
                        name,
                        data: rows.map((row) => row[metric]),
                        itemStyle: { color },
                    },
                    ...(showLastYear
                        ? [
                              {
                                  type: "bar",
                                  name: lastYearName,
                                  data: rows.map(
                                      (row) => row[`${metric}LastYear`],
                                  ),
                                  itemStyle: {
                                      color: `${color}59`,
                                  },
                              },
                          ]
                        : []),
                ];
            }),
        };
    }, [
        data,
        metrics,
        aggregation,
        showLastYear,
        hotelID,
        othersFrom,
        othersOrderBy,
        currentSeriesLabel,
        comparisonSeriesLabel,
    ]);

    return (
        <>
            <InfoIcon title={title} description={description} />

            <Box
                component={HoverPaper}
                sx={{
                    p: 2,
                    width: "100%",
                    minWidth: 0,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {chartData.xAxisData.length > 0 ? (
                    <EChartsReact
                        style={{
                            width: "100%",
                            height: 320,
                        }}
                        option={{
                            tooltip,
                            legend: {
                                bottom: 0,
                                left: "center",
                            },
                            grid: {
                                top: 20,
                                left: "5%",
                                right: "5%",
                                bottom: 50,
                                containLabel: true,
                            },
                            xAxis: {
                                type: "category",
                                data: chartData.xAxisData,
                                axisLabel: {
                                    interval: showAllXAxisLabels ? 0 : "auto",
                                    rotate: showAllXAxisLabels ? 45 : 0,
                                },
                            },
                            yAxis: {},
                            series: chartData.series,
                        }}
                    />
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text={emptyMessage} />
                        </TableBody>
                    </Table>
                )}
            </Box>
        </>
    );
};

export default ReportMetricVSMetricBars;
